/**
 * /promos/new — create a slot override (ข่าวประชาสัมพันธ์).
 *
 * 4 steps in one form:
 *   1. Pick page (dropdown) → loads layout
 *   2. Pick slot (flat list from collectSlotsClient — admin clicks the slot
 *      they want to override)
 *   3. Pick source: tabbed [Article | Inline]
 *      - Article — opens existing ArticlePickerDialog
 *      - Inline — title/thumb/link inputs
 *   4. Window: start + end datetime-local with showPicker() workaround
 *
 * On submit: POST /api/admin/slot-overrides → on 201 navigate to /promos;
 * on 409 OVERLAP_CONFLICT show inline alert (also surfaced via toast).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft, FileText, Repeat, Save } from 'lucide-react';
import type {
  GroupContentSummary,
  HydratedArticleItem,
  PageSummary,
  SlotOverrideCreateInput,
} from '@news-cms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArticlePickerDialog } from '@/components/group-contents/article-picker-dialog';
import { useSession } from '@/lib/auth';
import { datetimePickerHandlers } from '@/lib/datetime-picker';
import { groupContentsListQueryOptions } from '@/lib/group-contents';
import { collectCarouselsClient, collectSlotsClient } from '@/lib/page-layout-walk';
import { pageDetailQueryOptions, pagesListQueryOptions } from '@/lib/pages';
import { hasPermission } from '@/lib/permissions';
import { useCreateSlotOverride } from '@/lib/slot-overrides';

/** Optional pre-fill from deep-link query params (e.g. clicked from a
 *  SlotGroupEditor PR tab). When present, skips the page + target picker
 *  steps and lands on Step 3 (source) directly. */
interface NewPromoSearch {
  pageSlug?: string;
  slotId?: string;
  slideIndex?: number;
}

export const Route = createFileRoute('/_authenticated/promos/new')({
  validateSearch: (search: Record<string, unknown>): NewPromoSearch => ({
    pageSlug: typeof search.pageSlug === 'string' ? search.pageSlug : undefined,
    slotId: typeof search.slotId === 'string' ? search.slotId : undefined,
    slideIndex: typeof search.slideIndex === 'number' ? search.slideIndex : undefined,
  }),
  component: NewPromoPage,
});

type SourceKind = 'article' | 'inline';

/** Discriminated target picked by the admin — either an entire Slot
 *  (slideIndex=null) or a specific slide inside a Carousel. */
interface PickedTarget {
  slotId: string;
  slideIndex: number | null;
}

interface TargetRow {
  /** Stable key for React + selection state. */
  key: string;
  slotId: string;
  /** null when targeting a Slot. */
  slideIndex: number | null;
  /** Carousel id when targeting a slide; null when targeting a Slot. */
  carouselId: string | null;
  kind: 'slot' | 'carousel-slide';
  groupSlug: string;
  preset: string;
  /** Slot's itemIndex (slot rows only); undefined for carousel slides. */
  itemIndex?: number;
}

function NewPromoPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { user } = useSession();
  const canManage = hasPermission(user, 'promos.manage');
  const create = useCreateSlotOverride();

  const [pageId, setPageId] = useState<string>('');
  const [picked, setPicked] = useState<PickedTarget | null>(null);
  const [sourceKind, setSourceKind] = useState<SourceKind>('article');

  // Article source state.
  const [pickedArticle, setPickedArticle] = useState<HydratedArticleItem | null>(null);
  const [articlePickerOpen, setArticlePickerOpen] = useState(false);

  // Inline source state.
  const [inlineTitle, setInlineTitle] = useState('');
  const [inlineThumb, setInlineThumb] = useState('');
  const [inlineLink, setInlineLink] = useState('');

  // Window — datetime-local emits "YYYY-MM-DDTHH:mm" (no seconds, no TZ).
  const [startsAtLocal, setStartsAtLocal] = useState('');
  const [endsAtLocal, setEndsAtLocal] = useState('');

  const [formError, setFormError] = useState<string | null>(null);

  // Load list of pages + groups (for carousel slide-count lookup).
  const pagesQuery = useQuery(pagesListQueryOptions({ page: 1, pageSize: 100 }));
  const groupsQuery = useQuery(groupContentsListQueryOptions({ page: 1, pageSize: 100 }));
  const pages: PageSummary[] = pagesQuery.data?.items ?? [];
  const selectedPage = pages.find((p) => p.id === pageId) ?? null;

  // Pre-fill from query params (?pageSlug=&slotId=&slideIndex=) — used by
  // the SlotGroupEditor "PR" tab deep-link. Runs ONCE when pages arrive so
  // we can resolve pageSlug→id; manual changes thereafter aren't blocked.
  const prefilled = pages.length > 0 && pageId !== '';
  useEffect(() => {
    if (prefilled) return;
    if (!search.pageSlug || pages.length === 0) return;
    const match = pages.find((p) => p.slug === search.pageSlug);
    if (!match) return;
    setPageId(match.id);
    if (search.slotId) {
      setPicked({
        slotId: search.slotId,
        slideIndex: typeof search.slideIndex === 'number' ? search.slideIndex : null,
      });
    }
  }, [pages, search.pageSlug, search.slotId, search.slideIndex, prefilled]);

  const groupBySlug = useMemo(() => {
    const map = new Map<string, GroupContentSummary>();
    for (const g of groupsQuery.data?.items ?? []) map.set(g.slug, g);
    return map;
  }, [groupsQuery.data]);

  // Load the selected page's layout to extract slot + carousel ids.
  const pageDetailQuery = useQuery({
    ...pageDetailQueryOptions(pageId || '__none__'),
    enabled: Boolean(pageId),
  });

  /** Flatten layout into target rows: every Slot becomes one row; every
   *  Carousel becomes N rows (one per slide 0..maxCount-1). */
  const targets: TargetRow[] = useMemo(() => {
    const detail = pageDetailQuery.data;
    if (!detail) return [];
    const layout = detail.layout;
    const out: TargetRow[] = [];
    for (const s of collectSlotsClient(layout)) {
      out.push({
        key: s.id,
        slotId: s.id,
        slideIndex: null,
        carouselId: null,
        kind: 'slot',
        groupSlug: s.groupSlug,
        preset: s.preset,
        itemIndex: s.itemIndex,
      });
    }
    for (const c of collectCarouselsClient(layout)) {
      const maxCount = groupBySlug.get(c.groupSlug)?.maxCount ?? 6;
      for (let i = 0; i < maxCount; i++) {
        out.push({
          key: `${c.id}#${i}`,
          slotId: c.id,
          slideIndex: i,
          carouselId: c.id,
          kind: 'carousel-slide',
          groupSlug: c.groupSlug,
          preset: c.preset,
        });
      }
    }
    return out;
  }, [pageDetailQuery.data, groupBySlug]);

  const pickedKey = picked
    ? picked.slideIndex === null
      ? picked.slotId
      : `${picked.slotId}#${picked.slideIndex}`
    : null;

  // Target picker pagination — long pages can produce hundreds of rows
  // (slots + carousel slides 0..maxCount-1 each), so cap at 10 per page.
  const TARGETS_PER_PAGE = 10;
  const [targetsPage, setTargetsPage] = useState(1);
  const totalTargetPages = Math.max(1, Math.ceil(targets.length / TARGETS_PER_PAGE));
  const targetsStart = (targetsPage - 1) * TARGETS_PER_PAGE;
  const visibleTargets = targets.slice(targetsStart, targetsStart + TARGETS_PER_PAGE);

  // When `picked` is set programmatically (deep-link prefill) jump to the
  // page containing it so the row is immediately visible.
  useEffect(() => {
    if (!picked || targets.length === 0) return;
    const idx = targets.findIndex(
      (t) => t.slotId === picked.slotId && t.slideIndex === picked.slideIndex,
    );
    if (idx === -1) return;
    const targetPage = Math.floor(idx / TARGETS_PER_PAGE) + 1;
    setTargetsPage(targetPage);
  }, [picked, targets]);

  // Reset to page 1 whenever the underlying page changes.
  useEffect(() => {
    setTargetsPage(1);
  }, [pageId]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!selectedPage) {
      setFormError('กรุณาเลือกหน้า');
      return;
    }
    if (!picked) {
      setFormError('กรุณาเลือก slot หรือ carousel slide');
      return;
    }
    if (!startsAtLocal || !endsAtLocal) {
      setFormError('กรุณาเลือกช่วงเวลา');
      return;
    }
    const startsAt = new Date(startsAtLocal).toISOString();
    const endsAt = new Date(endsAtLocal).toISOString();
    if (new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
      setFormError('เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม');
      return;
    }

    let source: SlotOverrideCreateInput['source'];
    if (sourceKind === 'article') {
      if (!pickedArticle) {
        setFormError('กรุณาเลือกบทความ');
        return;
      }
      source = { kind: 'article', postId: pickedArticle.id };
    } else {
      if (!inlineTitle.trim()) {
        setFormError('กรุณาใส่หัวข้อ');
        return;
      }
      if (!inlineLink.trim()) {
        setFormError('กรุณาใส่ลิงก์');
        return;
      }
      source = {
        kind: 'inline',
        title: inlineTitle.trim(),
        thumbUrl: inlineThumb.trim() || null,
        linkUrl: inlineLink.trim(),
      };
    }

    const payload: SlotOverrideCreateInput = {
      pageSlug: selectedPage.slug,
      slotId: picked.slotId,
      slideIndex: picked.slideIndex,
      source,
      startsAt,
      endsAt,
    };
    create.mutate(payload, {
      onSuccess: () => {
        void navigate({ to: '/promos' });
      },
    });
  };

  if (!canManage) {
    return (
      <div className="p-8 text-center text-sm text-slate-500">
        ไม่มีสิทธิ์เข้าถึง — ต้องมี <code>promos.manage</code>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/promos" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
            <ArrowLeft className="mr-1 h-4 w-4" /> กลับ
          </Link>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
            สร้างข่าวประชาสัมพันธ์
          </h1>
        </div>

        <form
          onSubmit={submit}
          className="space-y-6 rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900"
        >
          {/* Step 1 — Page picker */}
          <div className="space-y-1.5">
            <Label htmlFor="promo-page">1. หน้า (Page) *</Label>
            <select
              id="promo-page"
              value={pageId}
              onChange={(e) => {
                setPageId(e.target.value);
                setPicked(null);
              }}
              className="border-input bg-background h-9 w-full rounded border px-2 text-sm"
            >
              <option value="">— เลือกหน้า —</option>
              {pages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} (/{p.slug}) — {p.slotCount} slots
                </option>
              ))}
            </select>
          </div>

          {/* Step 2 — Target picker: Slots and Carousel slides */}
          {pageId && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label>2. Slot หรือ Carousel slide ที่จะทับ *</Label>
                {targets.length > 0 && (
                  <span className="text-[10px] text-slate-400">
                    ทั้งหมด {targets.length} รายการ
                    {picked && (
                      <span className="ml-1 text-amber-600 dark:text-amber-400">· เลือกแล้ว 1</span>
                    )}
                  </span>
                )}
              </div>
              {pageDetailQuery.isLoading ? (
                <div className="text-xs text-slate-400">กำลังโหลด layout…</div>
              ) : targets.length === 0 ? (
                <div className="rounded border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
                  หน้านี้ยังไม่มี slot หรือ carousel — ให้ super_admin เพิ่มที่ /pages/
                  {selectedPage?.id ?? ''}
                  /edit ก่อน
                </div>
              ) : (
                <>
                  <div className="grid gap-1.5 sm:grid-cols-2">
                    {visibleTargets.map((t) => {
                      const active = pickedKey === t.key;
                      const Icon = t.kind === 'slot' ? FileText : Repeat;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setPicked({ slotId: t.slotId, slideIndex: t.slideIndex })}
                          className={`flex flex-col items-start gap-0.5 rounded border px-2.5 py-2 text-left text-xs transition ${
                            active
                              ? 'border-amber-400 bg-amber-50 dark:border-amber-500 dark:bg-amber-950/30'
                              : t.kind === 'carousel-slide'
                                ? 'border-indigo-200 bg-indigo-50/30 hover:bg-indigo-50 dark:border-indigo-900/40 dark:bg-indigo-950/10 dark:hover:bg-indigo-950/30'
                                : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800'
                          }`}
                        >
                          <div className="flex items-center gap-1">
                            <Icon
                              className={`h-3 w-3 ${
                                t.kind === 'carousel-slide' ? 'text-indigo-400' : 'text-slate-400'
                              }`}
                            />
                            <code className="text-[11px] font-medium">{t.slotId}</code>
                            {t.kind === 'carousel-slide' && (
                              <span className="rounded bg-indigo-200 px-1 text-[9px] font-medium text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200">
                                slide #{t.slideIndex}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-500">
                            group: <code>{t.groupSlug || '(ว่าง)'}</code>
                            {t.kind === 'slot' && t.itemIndex !== undefined && (
                              <> · item #{t.itemIndex}</>
                            )}{' '}
                            · {t.preset}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Pagination — only shown when >10 targets. */}
                  {totalTargetPages > 1 && (
                    <div className="flex items-center justify-between gap-2 pt-2 text-xs">
                      <span className="text-slate-500">
                        แสดง {targetsStart + 1}–
                        {Math.min(targetsStart + TARGETS_PER_PAGE, targets.length)} จาก{' '}
                        {targets.length}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={targetsPage <= 1}
                          onClick={() => setTargetsPage((p) => Math.max(1, p - 1))}
                        >
                          ก่อนหน้า
                        </Button>
                        <span className="px-2 text-slate-500">
                          หน้า {targetsPage}/{totalTargetPages}
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={targetsPage >= totalTargetPages}
                          onClick={() => setTargetsPage((p) => Math.min(totalTargetPages, p + 1))}
                        >
                          ถัดไป
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 3 — Source */}
          {picked && (
            <div className="space-y-2">
              <Label>3. แหล่งเนื้อหา *</Label>
              <div className="flex border-b border-slate-200 dark:border-slate-700">
                {(['article', 'inline'] as const).map((k) => {
                  const active = sourceKind === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setSourceKind(k)}
                      className={`px-3 py-1.5 text-xs transition ${
                        active
                          ? 'border-b-2 border-amber-500 text-amber-700 dark:text-amber-300'
                          : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      {k === 'article' ? '📰 บทความใน CMS' : '🔗 Inline (กรอกเอง)'}
                    </button>
                  );
                })}
              </div>

              {sourceKind === 'article' && (
                <div className="space-y-2 pt-1">
                  {pickedArticle ? (
                    <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
                      <div className="text-xs text-slate-400">เลือกแล้ว:</div>
                      <div className="font-medium text-slate-900 dark:text-slate-50">
                        {pickedArticle.title}
                      </div>
                      <div className="text-xs text-slate-500">/{pickedArticle.slug}</div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setArticlePickerOpen(true)}
                        className="mt-1.5"
                      >
                        เปลี่ยน
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setArticlePickerOpen(true)}
                    >
                      เลือกบทความ…
                    </Button>
                  )}
                </div>
              )}

              {sourceKind === 'inline' && (
                <div className="space-y-2 pt-1">
                  <div className="space-y-1.5">
                    <Label htmlFor="inline-title" className="text-xs">
                      หัวข้อ *
                    </Label>
                    <Input
                      id="inline-title"
                      value={inlineTitle}
                      onChange={(e) => setInlineTitle(e.target.value)}
                      maxLength={200}
                      placeholder="เช่น โปรโมชั่นพิเศษวันแม่"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="inline-thumb" className="text-xs">
                      Thumbnail URL (optional)
                    </Label>
                    <Input
                      id="inline-thumb"
                      value={inlineThumb}
                      onChange={(e) => setInlineThumb(e.target.value)}
                      placeholder="https://…"
                      maxLength={2000}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="inline-link" className="text-xs">
                      ลิงก์ปลายทาง *
                    </Label>
                    <Input
                      id="inline-link"
                      value={inlineLink}
                      onChange={(e) => setInlineLink(e.target.value)}
                      placeholder="https://…"
                      maxLength={2000}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4 — Window */}
          {picked && (
            <div className="space-y-2">
              <Label>4. ช่วงเวลาแสดง PR *</Label>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="starts-at" className="text-xs">
                    เริ่ม
                  </Label>
                  <Input
                    id="starts-at"
                    type="datetime-local"
                    value={startsAtLocal}
                    onChange={(e) => setStartsAtLocal(e.target.value)}
                    {...datetimePickerHandlers}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ends-at" className="text-xs">
                    สิ้นสุด
                  </Label>
                  <Input
                    id="ends-at"
                    type="datetime-local"
                    value={endsAtLocal}
                    onChange={(e) => setEndsAtLocal(e.target.value)}
                    {...datetimePickerHandlers}
                  />
                </div>
              </div>
              <p className="text-[10px] text-slate-400">
                เวลาเป็น local timezone — แปลงเป็น UTC เมื่อบันทึก. หลังเวลาสิ้นสุด slot
                กลับเป็นเนื้อหาปกติเอง
              </p>
            </div>
          )}

          {formError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-2">
            <Button type="submit" disabled={create.isPending}>
              <Save className="mr-1 h-4 w-4" />
              {create.isPending ? 'กำลังบันทึก…' : 'สร้าง PR'}
            </Button>
          </div>
        </form>
      </div>

      <ArticlePickerDialog
        open={articlePickerOpen}
        onOpenChange={setArticlePickerOpen}
        pressReleaseOnly
        onPick={(article) => {
          setPickedArticle(article);
          setArticlePickerOpen(false);
        }}
      />
    </div>
  );
}
